/**
 * eNVD Design System — CircularProgress component
 *
 * The circular progress ring (Figma *Progress Indicator / Circular*). Three
 * modes, mutually exclusive (see ADR 0026):
 *
 * - **Value mode** (`value`) — a determinate partial ring from 0–100%, with
 *   an optional center `showValue` number or custom `icon`.
 * - **Status mode** (`status`) — a full solid ring in `success`/`warning`/
 *   `danger`, reusing the DS-wide status vocabulary, with a built-in default
 *   icon (check/wifi-off/pause) that `icon` can override.
 * - **Indeterminate mode** (`indeterminate`) — a continuously spinning
 *   fading-tail ring (a CSS conic-gradient, not the SVG arc the other two
 *   modes use) for unknown-duration loading, with an optional center `icon`.
 *
 * @example
 * <CircularProgress value={42} size="lg" showValue aria-label="Upload progress" />
 *
 * @example
 * <CircularProgress status="success" size="xl" aria-label="Upload complete" />
 *
 * @example
 * <CircularProgress indeterminate size="xxl" icon={<TagSearchIcon />} aria-label="Loading" />
 */

import Typography, { type TypographyVariant } from '@ds/components/typography/Typography';
import { CheckIcon, type IconProps, PauseIcon, WifiOffIcon } from '@ds/icons';
import clsx from 'clsx';
import React, { forwardRef, type ReactNode } from 'react';

import styles from './CircularProgress.module.css';

export type CircularProgressSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl' | 'xxl';
export type CircularProgressStatus = 'success' | 'warning' | 'danger';

type IconComponent = React.ComponentType<IconProps>;

/** Ring diameter / stroke width / icon size (px) and value-label Typography variant per size step. */
const SIZE_CONFIG: Record<
    CircularProgressSize,
    { diameter: number; stroke: number; icon: number; valueVariant?: TypographyVariant }
> = {
    xs: { diameter: 16, stroke: 2, icon: 0 },
    sm: { diameter: 24, stroke: 3, icon: 0 },
    md: { diameter: 32, stroke: 4, icon: 16, valueVariant: 'tag' }, // stroke = --border-width-heavy (4px); keep in step
    lg: { diameter: 48, stroke: 5, icon: 20, valueVariant: 'title-2' },
    xl: { diameter: 64, stroke: 6, icon: 24, valueVariant: 'title-1' },
    xxl: { diameter: 96, stroke: 8, icon: 36, valueVariant: 'heading-1' },
};

/** Each status maps onto the DS-wide status vocabulary, with a built-in default icon. */
const STATUS_ICON: Record<CircularProgressStatus, IconComponent> = {
    success: CheckIcon,
    warning: WifiOffIcon,
    danger: PauseIcon,
};

interface CircularProgressCommonProps
    extends Omit<
        React.HTMLAttributes<HTMLDivElement>,
        'children' | 'aria-valuenow' | 'aria-valuemin' | 'aria-valuemax'
    > {
    /**
     * Ring diameter and stroke width. Neither `xs` nor `sm` shows center content —
     * they are too small to fit an icon or a three-digit value legibly.
     * @default 'md'
     */
    size?: CircularProgressSize;
    /**
     * Overrides the ring/spinner stroke and the center icon/value colour. Pass a token custom
     * property name from `tokens.css` (e.g. `'--status-info'`), resolved via `var(...)`
     * internally — not a raw CSS colour. In status mode, overrides the status's built-in colour.
     */
    color?: string;
    /** Accessible name — required, since color/icon alone isn't announced. */
    'aria-label': string;
    className?: string;
}

type CircularProgressCenterProps =
    | { showValue?: undefined; icon?: undefined }
    | { showValue: true; icon?: undefined }
    | { showValue?: undefined; icon: ReactNode };

export type CircularProgressValueProps = CircularProgressCommonProps &
    CircularProgressCenterProps & {
        /** Current progress, 0–100. Values outside the range are clamped. */
        value: number;
        status?: undefined;
        indeterminate?: undefined;
    };

export interface CircularProgressStatusProps extends CircularProgressCommonProps {
    /** A terminal state — renders a full ring in the matching status color, ignoring `value`. */
    status: CircularProgressStatus;
    value?: undefined;
    indeterminate?: undefined;
    /** Overrides the status's built-in default icon. */
    icon?: ReactNode;
}

export interface CircularProgressIndeterminateProps extends CircularProgressCommonProps {
    /** Unknown-duration loading — a continuously spinning fading-tail ring. Ignores `value`/`status`. */
    indeterminate: true;
    value?: undefined;
    status?: undefined;
    showValue?: undefined;
    /** Optional center icon, shown static while the ring spins. */
    icon?: ReactNode;
}

export type CircularProgressProps =
    | CircularProgressValueProps
    | CircularProgressStatusProps
    | CircularProgressIndeterminateProps;

/** Resolves the `color` prop (a token custom property name) to the `--circular-progress-color` override. */
const colorStyle = (color: string | undefined): React.CSSProperties | undefined =>
    color ? ({ '--circular-progress-color': `var(${color})` } as React.CSSProperties) : undefined;

/**
 * The center icon/value slot, shared by all three modes. Suppressed entirely at `xs` and `sm` —
 * neither ring is wide enough for an icon or a three-digit value.
 */
const CenterContent: React.FC<{ size: CircularProgressSize; node: ReactNode }> = ({ size, node }) => {
    const { icon: iconSize } = SIZE_CONFIG[size];
    if (node == null || size === 'xs' || size === 'sm') {
        return null;
    }
    return (
        <span
            className={styles.content}
            style={iconSize ? ({ '--circular-progress-icon-size': `${iconSize}px` } as React.CSSProperties) : undefined}
            aria-hidden="true"
        >
            {node}
        </span>
    );
};

/** The determinate SVG arc used by value mode and status mode. */
const Ring: React.FC<{
    size: CircularProgressSize;
    dashOffset: number;
    ringClassName?: string;
    centerNode: ReactNode;
}> = ({ size, dashOffset, ringClassName, centerNode }) => {
    const { diameter, stroke } = SIZE_CONFIG[size];
    const radius = diameter / 2 - stroke / 2;
    const center = diameter / 2;

    return (
        <>
            <svg
                className={styles.svg}
                viewBox={`0 0 ${diameter} ${diameter}`}
                width="100%"
                height="100%"
                aria-hidden="true"
                focusable="false"
            >
                <circle
                    className={styles.track}
                    cx={center}
                    cy={center}
                    r={radius}
                    strokeWidth={stroke}
                />
                <circle
                    className={clsx(styles.indicator, ringClassName)}
                    cx={center}
                    cy={center}
                    r={radius}
                    strokeWidth={stroke}
                    pathLength={100}
                    strokeDasharray="100 100"
                    strokeDashoffset={dashOffset}
                />
            </svg>
            <CenterContent
                size={size}
                node={centerNode}
            />
        </>
    );
};

const CircularProgress = forwardRef<HTMLDivElement, CircularProgressProps>((props, ref) => {
    if (props.indeterminate) {
        const { size = 'md', color, icon, className, indeterminate: _indeterminate, ...domRest } = props;
        const { stroke } = SIZE_CONFIG[size];

        return (
            <div
                ref={ref}
                role="progressbar"
                aria-busy="true"
                data-size={size}
                data-indeterminate=""
                className={clsx(styles.root, className)}
                style={
                    {
                        '--circular-progress-stroke': `${stroke}px`,
                        ...colorStyle(color),
                    } as React.CSSProperties
                }
                {...domRest}
            >
                <span
                    className={styles.spinner}
                    aria-hidden="true"
                />
                <CenterContent
                    size={size}
                    node={icon}
                />
            </div>
        );
    }

    if (props.status !== undefined) {
        const { size = 'md', status, color, icon, className, ...domRest } = props;
        const StatusIcon = STATUS_ICON[status];

        return (
            <div
                ref={ref}
                role="img"
                data-size={size}
                data-status={status}
                className={clsx(styles.root, className)}
                style={colorStyle(color)}
                {...domRest}
            >
                <Ring
                    size={size}
                    dashOffset={0}
                    ringClassName={styles[status]}
                    centerNode={icon ?? <StatusIcon />}
                />
            </div>
        );
    }

    const { size = 'md', value, showValue, icon, color, className, ...domRest } = props;
    const clampedValue = Math.min(100, Math.max(0, value));
    const cfg = SIZE_CONFIG[size];
    // No `color` prop on purpose: Typography's default `primary` is deliberately rule-less, so the
    // number inherits `.content` — the one chain that also governs the icons and `color`.
    // Setting an explicit colour here would break both.
    const centerNode =
        icon ??
        (showValue ? (
            <Typography
                variant={cfg.valueVariant ?? 'caption'}
                component="span"
                className={styles.value}
            >
                {Math.round(clampedValue)}
            </Typography>
        ) : null);

    return (
        <div
            ref={ref}
            role="progressbar"
            aria-valuenow={Math.round(clampedValue)}
            aria-valuemin={0}
            aria-valuemax={100}
            data-size={size}
            className={clsx(styles.root, className)}
            style={colorStyle(color)}
            {...domRest}
        >
            <Ring
                size={size}
                dashOffset={100 - clampedValue}
                centerNode={centerNode}
            />
        </div>
    );
});

CircularProgress.displayName = 'CircularProgress';

export { CircularProgress };
export default CircularProgress;
